import React, {
  PropsWithChildren,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import * as yup from "yup";
import update from "immutability-helper";
import ErrorText from "../../components/errorText";
import {
  createNewLead,
  getLeadDetail,
  updateLead,
  putUserPreferences,
} from "../../services/leadService";
import styled from "styled-components";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import Inputs from "../../components/inputs";
import { setCustomSource } from "../../actions/actions";
import InputMaps from "../../components/InputMaps";
import GroupField from "../../components/GroupField";
import moment from "moment";
import { Spin } from "antd";
import { isDisabled } from "@testing-library/user-event/dist/utils";
import { IoIosCloseCircle } from "react-icons/io";
import { Check, Plus, X } from "lucide-react";
import logger, { Logger } from '../../utils/logger';

export interface CreateLeadDataI {
  name: string;
  email: string;
  phone: string;
  notes: string;
  salesValue: number;
  integration?: string;
  multi_select?: string[];
  products?: Array<{id: string, quantity: number}>;
  saleValue?: string;
}

interface PropsT {
  showCreateNewLeadDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  getLeads: (leadId: any) => void;
  isEditMode: boolean;
  isDetailsBox: boolean;
  leadToBeUpdate: string;
  handleDrawerClick: () => void;
  selectedLeadId: string;
  leadList: Array<any>;
  disabled?: boolean;
  ref?: any;
  onClose?: () => void;
}

const CreateLeadForm: React.FC<PropsWithChildren<PropsT>> = forwardRef(
  (
    {
      showCreateNewLeadDrawer,
      getLeads,
      isEditMode,
      isDetailsBox,
      leadToBeUpdate,
      handleDrawerClick,
      selectedLeadId,
      leadList,
      disabled,
      onClose,
    },
    ref
  ) => {
    // ✅ PERFORMANCE FIX: Use targeted selectors instead of selecting entire rootReducers
    const customForm = useSelector((state: any) => state?.rootReducers?.user?.userPreferences?.customForm);
    const sourceList = useSelector((state: any) => state?.rootReducers?.customSource?.sourceList);

    // Backward compatible StoreData object (memoized would be ideal but keeping simple for now)
    const StoreData = {
      user: { userPreferences: { customForm } },
      customSource: { sourceList }
    };
    const dispatch = useDispatch();
    const [dynamicData, setDynamicData] = useState<any>([]);
    const [leadSourceData, setLeadSourceData] = useState([]);

    const [leadData, setLeadData] = useState<any>({
      name: "",
      email: "",
      phone: "",
      salesValue: 0,
      notes: "",
      integration: "64109561e6c7e737227a6382",
      list: ""
    });

    const [errors, setErrors] = useState<{[key: string]: string}>({
      name: "",
      email: "",
      saleValue: "",
      notes: "",
    });
    
    const regExp = /\b\d{10}\b/;
    const [newSourceToggle, setNewSourceToggle] = useState(false);
    const [selectedSource, setSelectedSource] = useState("leadForm");
    const [sourceName, setSourceName] = useState("");
    const [selectedList, setSelectedList] = useState("");
    const [addressName, setAddressName] = useState({});
    const [isPageLoad, setIsPageLoad] = useState<boolean>(false);
    const [leadListData, setleadListData] = useState<Array<string>>([]);

    // ============================================
    // 🔧 NEW: DYNAMIC VALIDATION SCHEMA CREATION
    // ============================================
    
    /**
     * Creates dynamic Yup validation schema based on custom fields from Redux
     * Respects the isRequired flag for each field type
     * NOW SUPPORTS: Group fields with nested subFields
     * @param customFields - Array of custom fields from StoreData
     * @returns Yup validation schema object
     */
    const createDynamicValidationSchema = (customFields: any[]) => {
      logger.log('🔍 Creating dynamic validation schema...');
      logger.log('📋 Custom fields to process:', customFields?.length || 0);

      // Start with base required fields (always required)
      let schemaFields: any = {
        name: yup.string().required("Name is required"),
      };

      // Process each custom field from Redux
      customFields?.forEach((field, index) => {
        logger.log(`📝 Processing field ${index + 1}:`, {
          label: field.label,
          value: field.value,
          type: field.type,
          isRequired: field.isRequired,
          readOnly: field.read_only,
          hasSubFields: !!field.subFields
        });

        // ============================================
        // 🆕 HANDLE GROUP FIELDS
        // ============================================
        if (field.type === 'group' && field.subFields) {
          logger.log(`📦 Processing group field: ${field.label} with ${field.subFields.length} subfields`);

          // Create nested schema for group
          const groupSchema: any = {};

          field.subFields.forEach((subField: any) => {
            // Skip conditional fields in validation (they'll be validated only if visible)
            if (subField.isShowConditionally) {
              logger.log(`⏭️ Skipping conditional subfield: ${subField.name}`);
              return;
            }

            // Only add validation if subfield is required
            if (subField.isRequired) {
              logger.log(`✅ Adding validation for group subfield: ${field.label}.${subField.name}`);

              switch (subField.type) {
                case 'email':
                  groupSchema[subField.value] = yup.string()
                    .email(`${subField.name} must be a valid email`)
                    .required(`${subField.name} is required`);
                  break;

                case 'phone':
                  groupSchema[subField.value] = yup.string()
                    .matches(/^\d{10,15}$/, `${subField.name} must be a valid phone number`)
                    .required(`${subField.name} is required`);
                  break;

                case 'amount':
                case 'number':
                case 'quantity':
                  groupSchema[subField.value] = yup.number()
                    .min(0, `${subField.name} must be a positive number`)
                    .required(`${subField.name} is required`);
                  break;

                case 'selection':
                  groupSchema[subField.value] = yup.string()
                    .required(`Please select ${subField.name}`);
                  break;

                case 'url':
                  groupSchema[subField.value] = yup.string()
                    .matches(/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}/, `${subField.name} must be a valid URL`)
                    .required(`${subField.name} is required`);
                  break;

                case 'multi-selection':
                  groupSchema[subField.value] = yup.array()
                    .min(1, `Please select at least one ${subField.name}`)
                    .required(`Please select ${subField.name}`);
                  break;

                case 'date':
                  groupSchema[subField.value] = yup.date()
                    .required(`${subField.name} is required`);
                  break;

                case 'time':
                  groupSchema[subField.value] = yup.string()
                    .required(`${subField.name} is required`);
                  break;

                case 'location':
                  groupSchema[subField.value] = yup.object().shape({
                    address: yup.string().required(`${subField.name} is required`)
                  });
                  break;

                case 'text':
                case 'name':
                default:
                  groupSchema[subField.value] = yup.string()
                    .required(`${subField.name} is required`);
                  break;
              }
            }
          });

          // Add group schema to main schema
          if (Object.keys(groupSchema).length > 0) {
            schemaFields[field.value] = yup.object().shape(groupSchema);
            logger.log(`🎯 Added group validation for: ${field.label} with ${Object.keys(groupSchema).length} required subfields`);
          }

          return; // Skip regular field processing for groups
        }

        // ============================================
        // HANDLE REGULAR FIELDS (existing logic)
        // ============================================

        // Only add validation if field is required and not read-only
        if (field.isRequired && !field.read_only) {
          logger.log(`✅ Adding validation for required field: ${field.label}`);

          switch (field.type) {
            case 'email':
              schemaFields[field.value] = yup.string()
                .email(`${field.label} must be a valid email`)
                .required(`${field.label} is required`);
              break;

            case 'phone':
              schemaFields[field.value] = yup.string()
                .matches(/^\d{10,15}$/, `${field.label} must be a valid phone number`)
                .required(`${field.label} is required`);
              break;

            case 'amount':
            case 'number':
            case 'quantity':
              schemaFields[field.value] = yup.number()
                .min(0, `${field.label} must be a positive number`)
                .required(`${field.label} is required`);
              break;

            case 'selection':
              schemaFields[field.value] = yup.string()
                .required(`Please select ${field.label}`);
              break;

            case 'url':
              schemaFields[field.value] = yup.string()
                .matches(/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}/, `${field.label} must be a valid URL`)
                .required(`${field.label} is required`);
              break;

            case 'multi-selection':
              schemaFields[field.value] = yup.array()
                .min(1, `Please select at least one ${field.label}`)
                .required(`Please select ${field.label}`);
              break;

            case 'date':
              schemaFields[field.value] = yup.date()
                .required(`${field.label} is required`);
              break;

            case 'time':
              schemaFields[field.value] = yup.string()
                .required(`${field.label} is required`);
              break;

            case 'location':
              // For location fields, validate the address property
              schemaFields[field.value] = yup.object().shape({
                address: yup.string().required(`${field.label} is required`)
              });
              break;

            case 'text':
            case 'name':
            default:
              // For text, name, and other string-based types
              schemaFields[field.value] = yup.string()
                .required(`${field.label} is required`);
              break;
          }

          logger.log(`🎯 Added validation rule for: ${field.label} (${field.type})`);
        } else if (field.isRequired && field.read_only) {
          logger.log(`⚠️ Skipping read-only required field: ${field.label}`);
        } else {
          logger.log(`ℹ️ Skipping optional field: ${field.label}`);
        }
      });

      logger.log('📊 Final validation schema fields:', Object.keys(schemaFields));
      return yup.object().shape(schemaFields);
    };

    useEffect(() => {
      if (isEditMode || disabled) {
        fetchLeadDetails();
      }
      let form_Data = StoreData.user?.userPreferences?.customForm;
      let source_Data = StoreData.customSource?.sourceList;
      setDynamicData(form_Data);
      setLeadSourceData(source_Data);
    }, []);

    const fetchLeadDetails = async () => {
      logger.log('📥 Fetching lead details for editing...');
      const response = await getLeadDetail(leadToBeUpdate);
      if (response && response.status) {
        logger.log('✅ Lead details fetched successfully');
        logger.log('📋 Full response data:', response?.data?.data);

        // Process extraDetails to handle group fields (arrays → objects)
        const extraDetails = response?.data?.data.extraDetails || {};
        logger.log('📦 Raw extraDetails:', extraDetails);

        const processedExtraDetails = { ...extraDetails };

        // Build a set of group field values from customForm
        const groupFieldValues = new Set<string>();
        const customForm = StoreData.user?.userPreferences?.customForm || [];
        customForm.forEach((field: any) => {
          if (field.type === 'group') {
            groupFieldValues.add(field.value);
            logger.log(`🔍 Registered group field for editing: ${field.value}`);
          }
        });

        // Convert group field arrays to single objects for editing
        Object.keys(processedExtraDetails).forEach(key => {
          const isGroupField = groupFieldValues.has(key);

          if (isGroupField && Array.isArray(processedExtraDetails[key])) {
            // Take the first item from the array for editing
            if (processedExtraDetails[key].length > 0) {
              logger.log(`📦 Converting group field array to object: ${key}`, processedExtraDetails[key][0]);
              processedExtraDetails[key] = processedExtraDetails[key][0];
            } else {
              // Empty array → empty object
              logger.log(`⚠️ Empty group field array: ${key}`);
              processedExtraDetails[key] = {};
            }
          } else if (isGroupField) {
            logger.log(`📦 Group field already an object: ${key}`, processedExtraDetails[key]);
          }
        });

        logger.log('📦 Processed extraDetails:', processedExtraDetails);

        const finalLeadData = {
          ...leadData,
          ...processedExtraDetails,
          name: response?.data?.data?.name || "",
          email: response?.data?.data?.email || "",
          phone: response?.data?.data?.phone
            ? Number(response?.data?.data?.phone)
            : "",
          saleValue: response?.data?.data?.saleValue
            ? Number(response?.data?.data?.saleValue)
            : "",
          notes: response?.data?.data?.note || "",
        };

        logger.log('✅ Final leadData to set:', finalLeadData);
        setLeadData(finalLeadData);
        setSelectedSource(response.data.data?.customSource);
      }
    };

    const handleChange = (name: any, value: string | string[]) => {
      logger.log(`📝 Field changed: ${name} = ${value}`);
      setLeadData({
        ...leadData,
        [name]: value,
      });

      // Clear error when user starts typing
      setErrors({
        ...errors,
        [name]: "",
      });
    };

    const handleMapChange = (name: any, value: string, coords: any) => {
      logger.log(`🗺️ Location field changed: ${name}`);
      const locationObject = {
        address: value,
        coords: {
          latitude: coords.lat,
          longitude: coords.lng,
          latitudeDelta: 0.0922,
          longitudeDelta: 0.0922
        }
      };

      // FIXED: Merge with existing values instead of replacing
      setAddressName(prevState => ({
        ...prevState,
        [name]: locationObject
      }));

      setErrors({
        ...errors,
        [name]: "",
      });
    };

    /**
     * Handle changes to group subfields
     * @param groupValue - The group's value/key (e.g., "grp_2", "accessory")
     * @param fieldName - The subfield's name (e.g., "type", "hp")
     * @param value - The new value
     * @param coords - Optional coordinates for location fields
     */
    const handleGroupFieldChange = (groupValue: string, fieldName: string, value: any, coords?: any) => {
      logger.log(`📦 Group field changed: ${groupValue}.${fieldName} =`, value);

      // Handle location fields within groups
      if (coords) {
        const locationObject = {
          address: value,
          coords: {
            latitude: coords.lat,
            longitude: coords.lng,
            latitudeDelta: 0.0922,
            longitudeDelta: 0.0922
          }
        };
        value = locationObject;
      }

      // Update leadData with nested group structure
      setLeadData((prevData: any) => ({
        ...prevData,
        [groupValue]: {
          ...(prevData[groupValue] || {}),
          [fieldName]: value
        }
      }));

      // Clear error for this field
      const errorPath = `${groupValue}.${fieldName}`;
      setErrors((prevErrors) => ({
        ...prevErrors,
        [errorPath]: ""
      }));
    };

    const onSourceChange = (tab: string) => {
      setSelectedSource(tab);
    };

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    function removeNullAndBlankValues(obj: any): any {
      const newObj: any = {};
      for (const key in obj) {
        const value = obj[key];
        if (
          value !== null &&
          value !== "" &&
          value !== "0" &&
          !isEmptyObject(value) &&
          !isEmptyArray(value)
        ) {
          newObj[key] = value;
        }
      }
      return newObj;
    }

    function isEmptyObject(obj: any): boolean {
      return obj && typeof obj === "object" && Object.keys(obj).length === 0;
    }

    function isEmptyArray(value: any): boolean {
      if (Array.isArray(value)) {
        return (
          value.length === 0 || (value.length === 1 && isEmptyArray(value[0]))
        );
      }
      return typeof value === "object" && Object.keys(value).length === 0;
    }

    // ============================================
    // 🔧 ENHANCED: FORM SUBMISSION WITH DYNAMIC VALIDATION
    // ============================================
    
    /**
     * Enhanced form submission handler with dynamic validation
     * Validates all required fields based on Redux custom form configuration
     */
    const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
      e && e.preventDefault();
      logger.log('🚀 Form submission started...');
      logger.log('📊 Current form data:', leadData);
      logger.log('🎯 Dynamic fields for validation:', dynamicData?.length || 0);
      
      setIsPageLoad(true);
      
      // Include address data in leadData for validation
      if (Object.values(addressName)) {
        logger.log('🗺️ Including address data in validation');
        Object.assign(leadData, addressName);
      }
      
      // Create dynamic validation schema based on custom fields from Redux
      const dynamicSchema = createDynamicValidationSchema(dynamicData || []);
      
      try {
        logger.log('🔍 Validating form data against dynamic schema...');
        
        // Validate the form data against the dynamic schema
        await dynamicSchema.validate(leadData, { abortEarly: false });
        
        logger.log('✅ Validation passed! Proceeding with form submission...');
        
        // ========================================
        // EXISTING SUBMISSION LOGIC (PRESERVED)
        // ========================================
        
        const newLeadData: any = {
          name: leadData?.name,
          phone: leadData?.phone.toString(),
          email: leadData?.email,
          saleValue: leadData.saleValue ? Number(leadData.saleValue) : null,
          customSource: selectedSource,
          isDistributed: leadData.isDistributed || null,
          whatsApp: leadData.whatsApp || null,
           notes: leadData.notes || null,
          files: leadData.files || null,
          integration: leadData.integration || null,
          list: leadData.list || null,
          extraDetails: {},
          customField: [{}],
        };
logger.log('🔍 NOTES FIELD DEBUG:', {
  'leadData.notes': leadData.notes,
  'leadData.whatsApp': leadData.whatsApp,
  'newLeadData.notes': newLeadData.notes,
  'notes field exists in leadData': 'notes' in leadData
});
        // Loop through leadData object and add remaining fields to extraDetails
        // Loop through leadData object and add remaining fields to extraDetails
for (const key in leadData) {
  const value = leadData[key];
  
  if (!newLeadData.hasOwnProperty(key)) {
    // ✅ FIXED: Enhanced empty value checking (includes empty arrays)
    const isValidValue = value !== null && 
                        value !== "" && 
                        value !== 0 && 
                        !(Array.isArray(value) && value.length === 0) && // ✅ NEW: Filters empty arrays
                        !(typeof value === 'object' && Object.keys(value).length === 0);
    
    if (isValidValue) {
      logger.log(`✅ Including field: ${key} =`, value);
      newLeadData.extraDetails[key] = value;
    } else {
      logger.log(`⏭️ Skipping empty field: ${key} =`, value);
    }
  }
}
        
        Logger.log("newLeadData =====", newLeadData);
        const updateLeadData: any = removeNullAndBlankValues(newLeadData);
        
        if (isEditMode) {
  try {
    logger.log('📝 Updating existing lead...');
    // ✅ FIXED: DON'T remove integration for edits
    // const { integration, ...body } = updateLeadData; // ❌ Remove this line
    const editResponse = await updateLead(leadToBeUpdate, updateLeadData);
            if (editResponse && editResponse.status == 200) {
              toast.success(editResponse?.data?.message);
              handleDrawerClick();
              clearAllLeadForm();
              onClose && onClose();
              leadData.list !== "0"
                ? getLeads(leadData.list)
                : getLeads(undefined);
            }
          } catch (err: any) {
            console.error('❌ Error updating lead:', err);
            if (err?.response?.data?.message) {
              toast.error(err?.response?.data?.message);
            } else {
              toast.error("Error while updating lead!");
            }
          }
        } else {
          try {
            logger.log('➕ Creating new lead...');
            let body: any = updateLeadData;
            const response = await createNewLead(body);
            if (response && response.status == 200) {
              toast.success(response?.data?.message);
              handleDrawerClick();
              clearAllLeadForm();
              leadData.list !== "0"
                ? getLeads(leadData.list)
                : getLeads(undefined);
            }
          } catch (err: any) {
    console.error('❌ Error creating lead:', err);
    
    // Check for duplicate lead error (422)
    if (err?.response?.status === 422 && err?.response?.data?.data?.lead) {
      const lead = err?.response?.data?.data?.lead;
      
      // Safe field extraction with fallbacks
      const name = lead.name || 'Unknown';
      const phone = lead.phone || 'N/A';
      const email = lead.email || 'N/A';
      const status = (lead.status && lead.status.length > 0) ? lead.status[0] : 'N/A';
      const createdBy = lead.createdByName || 'Unknown';
      const createdDate = lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : 'N/A';
      
      // Show detailed toast with lead info
      toast.error(
        `Lead already exists!\n\nName: ${name}\nPhone: ${phone}\nEmail: ${email}\nStatus: ${status}\nCreated: ${createdDate}\nCreated by: ${createdBy}`,
        {
          duration: 8000,
          style: {
            maxWidth: '450px',
            whiteSpace: 'pre-line'
          }
        }
      );
      
    } else if (err?.response?.data?.message) {
      toast.error(err?.response?.data?.message);
    } else {
      toast.error("Error while creating a new lead!");
    }
  }
        }
        
      } catch (validationError: any) {
        logger.log('❌ Form validation failed!');
        logger.log('🚫 Validation errors:', validationError.errors);
        
        // Extract validation errors and map them to form fields
        const validationErrors = validationError.inner.reduce(
          (acc: any, error: { path: string; message: string }) => {
            logger.log(`🔴 Field error: ${error.path} - ${error.message}`);
            return {
              ...acc,
              [error.path]: error.message,
            };
          },
          {}
        );
        
        // Update the errors state to display validation messages
        setErrors((prevErrors) =>
          update(prevErrors, {
            $set: validationErrors,
          })
        );
        
        // Show user-friendly toast message
        toast.error('Please fill in all required fields marked with *');
        
        // Scroll to first error field (optional enhancement)
        const firstErrorField = Object.keys(validationErrors)[0];
        if (firstErrorField) {
          logger.log(`🎯 Focusing first error field: ${firstErrorField}`);
          const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            (errorElement as HTMLElement).focus();
          }
        }
      }
      
      setIsPageLoad(false);
      logger.log('🏁 Form submission process completed');
    };

    const clearAllLeadForm = () => {
      logger.log('🧹 Clearing all form data...');
      setLeadData({
        name: "",
        email: "",
        phone: "",
        salesValue: 0,
        notes: "",
      });
      setAddressName({});
      showCreateNewLeadDrawer(false);
    };

    const NewSourceToggle = () => {
      setNewSourceToggle(!newSourceToggle);
      setSourceName("");
    };

    const addNewSource = () => {
      if (sourceName) {
        let tempData: any = [...leadSourceData];
        let tempObj = {
          name: sourceName,
          value: sourceName?.split(" ")?.join("_"),
        };
        tempData.push(tempObj);
        handleAddLeadSource(tempData);
        dispatch(setCustomSource(tempData));
        setLeadSourceData(tempData);
        NewSourceToggle();
      }
    };

    const handleAddLeadSource = async (data: any) => {
      try {
        const response = await putUserPreferences({ customSource: data });
        if (response && response.status) {
          Logger.log(response.data.message);
        }
      } catch (err: any) {
        if (err?.response?.data?.message) {
          toast.error(err?.response?.data?.message);
        } else {
          toast.error("Error while updating Source!");
        }
      }
    };

    useEffect(() => {
      if (selectedLeadId) {
        setSelectedList(selectedLeadId);
        handleChange("list", selectedLeadId);
      }
    }, [selectedLeadId]);

    useEffect(() => {
      if (leadList) {
        let defaultLead = {
          name: "Default lead list",
          _id: "",
        };
        setleadListData([defaultLead, ...leadList]);
      }
    }, [leadList]);
    
    return (
      <Root>
        <Spin size="large" tip="Loading..." spinning={isPageLoad}>
          <form
            id="LeadsFilterForm"
            onSubmit={(e) => handleSubmit(e)}
            className="leadsform"
          >
            <div className="form-container">
              <div className="product_group lead_form">
                <div className="lead_source_header">
                  <h5 className="lead_source">Lead Source</h5>
                  
                  {!disabled && (
                    <div className="new_source_control">
                      {!newSourceToggle ? (
                        <button
                          className="new_source_btn"
                          type="button"
                          onClick={() => NewSourceToggle()}
                        >
                          <Plus size={14} className="icon-plus" />
                          <span>New Source</span>
                        </button>
                      ) : (
                        <div className="new_source_input">
                          <input
                            name="name"
                            type="text"
                            className="form-control"
                            autoComplete="off"
                            value={sourceName}
                            onChange={(e) => setSourceName(e?.target?.value)}
                            placeholder="Source name"
                          />
                          <div className="action_buttons">
                            <button className="btn-save" onClick={() => addNewSource()}>
                              <Check size={16} />
                            </button>
                            <button className="btn-cancel" onClick={() => NewSourceToggle()}>
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="lead_source_tabs">
                  <div className="source_tabs">
                    {leadSourceData?.map((item: any, i: number) => {
                      if (selectedSource !== item.value && disabled) return null;
                      
                      return (
                        <div
                          key={i}
                          onClick={() => onSourceChange(item.value)}
                          className={`tab_item ${selectedSource === item.value ? 'active' : ''}`}
                        >
                          {item.name}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div>
                    <label className="form-label">
                      Select List
                    </label>
                    <select
                      disabled={isEditMode || disabled}
                      name="cars"
                      className="form-control"
                      value={selectedList}
                      onChange={(e) => {
                        setSelectedList(e.target.value);
                        handleChange("list", e.target.value);
                      }}
                    >
                      {leadListData.map((item: any, ix: number) => {
                        return (
                          <option value={item._id} key={ix}>
                            {item.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  
                  {/* ============================================ */}
                  {/* 🔧 ENHANCED: DYNAMIC FIELDS WITH VALIDATION */}
                  {/* NOW SUPPORTS: Group fields with conditional visibility */}
                  {/* ============================================ */}

                  {dynamicData?.map((item: any, i: number) => {
                    // ============================================
                    // 🆕 HANDLE GROUP FIELDS
                    // ============================================
                    if (item.type === "group" && item.subFields) {
                      logger.log(`📦 Rendering group field: ${item.label}`);
                      return (
                        <GroupField
                          key={i}
                          groupName={item.label || item.name}
                          groupValue={item.value}
                          subFields={item.subFields}
                          groupData={leadData?.[item.value] || {}}
                          onChange={handleGroupFieldChange}
                          errors={errors}
                          disabled={disabled}
                        />
                      );
                    }

                    // ============================================
                    // HANDLE REGULAR FIELDS (existing logic)
                    // ============================================

                    // Fix for handling location data properly
                    let finalValue;
                    if (item.type === "location") {
                      // If it's a location field, extract just the address string
                      const locationData = leadData?.[item?.value];
                      finalValue = locationData?.address || "";
                    } else if (item.type === "multi-selection") {
                      finalValue = leadData?.[item?.value] || [];
                    } else if (item.type === "quantity") {
                      // Handle quantity fields as numbers
                      finalValue = leadData?.[item?.value] || "";
                    } else {
                      finalValue = isNaN(leadData?.[item?.value])
                        ? leadData?.[item?.value?.toLowerCase()]
                        : leadData?.[item?.value];
                    }

                    return (
                      <div className="form-group hide_dt select_icon" key={i}>
                        {item.type === "location" ? (
                          <InputMaps
                            disabled={disabled}
                            type={item.type}
                            name={item?.value}
                            value={finalValue}
                            locationData={leadData?.[item?.value]}
                            onChange={(name: string, value: string, coords: any) => 
                              handleMapChange(name, value, coords)
                            }
                            className="form-control"
                            label={item.label}
                            isRequired={item?.isRequired}
                          />
                        ) : item.type === "date" ? (
                          <div className="form-group">
                            <label className="form-label">
                              {item?.label}
                              {/* ✅ Show red asterisk for required fields */}
                              {item?.isRequired && <span style={{ color: "red" }}>*</span>}
                            </label>
                            <input
                              disabled={disabled}
                              type="date"
                              id="task_date"
                              name="toBePerformAt"
                              className="form-control"
                              value={finalValue}
                              onChange={(e: any) =>
                                handleChange(item?.value, e.target.value)
                              }
                              placeholder="Enter Date"
                            />
                          </div>
                        ) : (
                          <Inputs
                            disabled={disabled}
                            name={item?.value}
                            type={
                              item.type === "phone" || item.type === "amount"
                                ? "number"
                                : item.type  // Keep 'quantity' as is for stepper rendering
                            }
                            autoComplete="off"
                            className={
                              item.label === "Notes"
                                ? "form-control note_area"
                                : "form-control"
                            }
                            placeholder={`Enter ${item?.name}`}
                            value={finalValue || ""}
                            onChange={(e: any) =>
                              handleChange(item?.value, e.target.value)
                            }
                            label={item.label}
                            isRequired={item.isRequired}
                            options={["selection", "multi-selection"].includes(item.type) ? item.options : undefined}
                            id={
                              item.type === "time" || item.type === "date"
                                ? "lead_date_time"
                                : ""
                            }
                          />
                        )}

                        {/* ============================================ */}
                        {/* ✅ FIXED: ERROR DISPLAY FOR REQUIRED FIELDS */}
                        {/* ============================================ */}
                        
                        {/* Show validation error if field has error and is required */}
                        {errors[item?.value] && item?.isRequired && (
                          <ErrorText message={errors[item?.value]} />
                        )}
                        
                        {/* Additional error handling for specific field types */}
                        {errors[item?.value] && !item?.isRequired && (
                          <ErrorText message={errors[item?.value]} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {!isDetailsBox || (isDetailsBox && isEditMode) ? (
                <div className="d-flex justify-content-center Leadscreate ">
                  <button type="submit" className="btn btn-dark w-100">
                    {!isEditMode ? "Add" : "Update"} Lead
                  </button>
                </div>
              ) : null}
            </div>
            <Toaster position="top-right" reverseOrder={false} />
          </form>
        </Spin>
      </Root>
    );
  }
);

export default CreateLeadForm;

// Styled components (preserved from original)
const Root = styled.section`
  .form-container {
    display: flex;
    flex-direction: column;
    padding-top: 0px;
  }
  
  /* Lead Form General Styling */
  .lead_form {
    margin-bottom: 0px;
    margin-top: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }
  
  /* Lead Source Header */
  .lead_source {
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 14px;
    color: #333;
  }
  
  /* Lead Source Tab Container */
  .lead_source_tabs {
    margin-bottom: 12px;
  }
  
  .source_tabs_wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
  }
  
  /* Tab Items Container */
   .source_tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 2px;
    flex-grow: 1;
  }
  
  /* Individual Tab Item */
  .tab_item {
    padding: 6px 12px;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    color: #495057;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
  }
  
  .tab_item:hover {
    background-color: #e9ecef;
  }
  
  .tab_item.active {
    background-color: #0d6efd;
    color: white;
    border-color: #0d6efd;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }
  
  .new_source_btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 0;
    background-color: transparent;
    border: none;
    color: #0d6efd;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .new_source_btn:hover {
    color: #0a58ca;
    text-decoration: underline;
  }

  .icon-plus {
    stroke-width: 2.5;
  }
  
  /* Input Field for New Source */
  .new_source_input {
    display: flex;
    width: 100%;
    max-width: 300px;
  }
  
  .new_source_input .form-control {
    border-radius: 20px 0 0 20px;
    border-right: none;
    padding: 6px 12px;
    font-size: 13px;
    height: 34px;
    flex-grow: 1;
  }
  
  /* Save/Cancel Buttons */
  .action_buttons {
    display: flex;
  }
  
  .action_buttons button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: 1px solid #dee2e6;
    background-color: white;
    cursor: pointer;
    padding: 0;
  }
  
  .btn-save {
    color: #28a745;
    border-radius: 0;
    border-left: none;
  }
  
  .btn-cancel {
    color: #dc3545;
    border-radius: 0 20px 20px 0;
    border-left: none;
  }
  
  .btn-save:hover {
    background-color: rgba(40, 167, 69, 0.1);
  }
  
  .btn-cancel:hover {
    background-color: rgba(220, 53, 69, 0.1);
  }
  
  /* Form Field General Styling */
  .form-label {
    display: block;
    margin-bottom: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #212529;
  }
  
  .form-control {
    position: relative;
    width: 100%;
    padding: 8px 12px;
    font-size: 14px;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: 4px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }
  
  .form-control:focus {
    color: #495057;
    background-color: #fff;
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
  
  /* Select Dropdown */
  select.form-control {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    padding-right: 30px;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 16px 12px;
  }
  
  /* Add Lead Button */
  .Leadscreate {
    position: sticky;
    margin-top: auto;
    bottom: 0px;
    padding-top: 16px;
  }
  
  .Leadscreate .btn {
    border-radius: 8px;
    padding: 12px 20px;
    font-weight: 600;
    font-size: 15px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .Leadscreate .btn-dark {
    background-color: #0d6efd;
    border-color: #0d6efd;
    color: white;
  }

  .lead_source_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .lead_source {
    margin-bottom: 0;
  }
  
  .Leadscreate .btn-dark:hover {
    background-color: #0b5ed7;
    border-color: #0a58ca;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0,0,0,0.15);
  }
  
  .Leadscreate .btn-dark:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
  
  /* Dropdown Menus */
  .dropdown-menu {
    position: absolute;
    width: 100%;
    z-index: 1000;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    padding: 6px 0;
  }

  .dropdown-item {
    padding: 8px 12px;
    cursor: pointer;
    font-size: 14px;
    &:hover {
      background-color: #f8f9fa;
    }
  }

  .form-check-input {
    margin-right: 8px;
  }

  .selected-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;